import React, { useRef, useState } from "react";
import Modal from "./Modal";
import { toast } from "sonner";
import { Smartphone, FileUp, Loader2, Info } from "lucide-react";
import api, { formatApiErrorDetail } from "../lib/api";
import { parseVCards, parseCSV, canPickContacts, pickPhoneContacts } from "../lib/contactImport";
import ImportReview from "./ImportReview";

const opt =
  "w-full flex items-start gap-4 p-4 rounded-xl bg-[#1E293B] border border-slate-700 hover:border-blue-500 hover:bg-slate-800 transition-colors text-left disabled:opacity-60";

function Option({ testId, onClick, busy, disabled, icon: Icon, color, title, desc }) {
  return (
    <button data-testid={testId} onClick={onClick} disabled={disabled} className={opt}>
      <div className={`w-11 h-11 rounded-lg ${color} flex items-center justify-center shrink-0`}>
        {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : <Icon className="w-5 h-5" />}
      </div>
      <div>
        <p className="text-sm font-semibold text-white">{title}</p>
        <p className="text-xs text-slate-400 mt-0.5">{desc}</p>
      </div>
    </button>
  );
}

export default function ImportDialog({ open, onClose, onImported }) {
  const [busy, setBusy] = useState(null);
  const [review, setReview] = useState(null);
  const fileRef = useRef(null);
  const picker = canPickContacts();

  const close = () => { setReview(null); onClose(); };

  const load = async (kind, fn) => {
    setBusy(kind);
    try {
      const [contacts, { data: existing }] = await Promise.all([fn(), api.get("/cards")]);
      if (!contacts.length) {
        toast.error(kind === "pick" ? "No one was picked. Tap a person's name in your contacts to choose them." : "No contacts found to import", { duration: 8000 });
        return;
      }
      setReview({ contacts, existing, preselect: kind === "pick" });
    } catch (e) {
      if (e?.name !== "AbortError") toast.error(`Couldn't read those contacts${e?.message ? `: ${e.message}` : ""}`, { duration: 8000 });
    } finally { setBusy(null); }
  };

  const addMore = async () => {
    setBusy("more");
    try {
      const more = await pickPhoneContacts(false);
      if (more.length) setReview((r) => ({ ...r, contacts: [...r.contacts, ...more] }));
    } catch (e) {
      if (e?.name !== "AbortError") toast.error("Couldn't open your contacts");
    } finally { setBusy(null); }
  };

  const save = async (cards) => {
    setBusy("save");
    try {
      let created = 0;
      let skipped = 0;
      for (let i = 0; i < cards.length; i += 1000) {
        const { data } = await api.post("/cards/import", { cards: cards.slice(i, i + 1000) });
        created += data.created;
        skipped += data.skipped;
      }
      toast.success(`Imported ${created} contact${created === 1 ? "" : "s"}${skipped ? ` · ${skipped} already in Dextyou` : ""}`);
      onImported?.();
      close();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Import failed");
    } finally { setBusy(null); }
  };

  const onFile = (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    load("file", async () => {
      const text = await file.text();
      return /BEGIN:VCARD/i.test(text) ? parseVCards(text) : parseCSV(text);
    });
  };

  return (
    <Modal open={open} onClose={close} maxWidth="max-w-md" testId="import-dialog">
      {review ? (
        <ImportReview contacts={review.contacts} existing={review.existing} preselect={review.preselect} onAddMore={review.preselect ? addMore : undefined} addingMore={busy === "more"} busy={busy === "save"} onBack={() => setReview(null)} onConfirm={save} />
      ) : (
        <div className="p-6">
          <p className="overline text-blue-500 mb-1">Bring your network in</p>
          <h2 className="font-heading text-2xl font-bold tracking-tight mb-1">Import from your phone</h2>
          <p className="text-sm text-slate-400 mb-6">Add people from your phone book to your Dextyou rolodex. You'll choose who to add next.</p>
          <div className="space-y-3">
            {picker && (
              <Option testId="import-pick-contacts-button" onClick={() => load("pick", () => pickPhoneContacts(false))} busy={busy === "pick"} disabled={!!busy}
                icon={Smartphone} color="bg-blue-600/15 text-blue-400" title="Pick from phone contacts" desc="Tap a person to add them. You can pick more people on the next screen." />
            )}
            <Option testId="import-file-button" onClick={() => fileRef.current?.click()} busy={busy === "file"} disabled={!!busy}
              icon={FileUp} color="bg-emerald-500/15 text-emerald-400" title="Upload a contacts file (.vcf or .csv)" desc="Works on iPhone, Android and computer." />
            <input ref={fileRef} type="file" accept=".vcf,.vcard,.csv,text/vcard,text/x-vcard,text/csv" className="hidden" onChange={onFile} data-testid="import-file-input" />
          </div>
          <div className="mt-5 rounded-xl border border-slate-800 bg-slate-900/50 p-4 text-xs text-slate-400 space-y-2" data-testid="import-help">
            <p className="flex items-center gap-1.5 text-slate-300 font-semibold"><Info className="w-3.5 h-3.5" /> How to get a contacts file</p>
            <p><span className="text-slate-200">iPhone:</span> open Contacts, tap Lists, long-press "All Contacts" → Export → Save to Files. Then upload it here.</p>
            <p><span className="text-slate-200">Android:</span> {picker ? "use the button above, or " : ""}open Contacts → Fix & manage → Export to file (.vcf).</p>
          </div>
        </div>
      )}
    </Modal>
  );
}
